import { Box, Stack } from "@mui/material";
import CustomDialog from "./CustomDialog";
import React, { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Webcam from "react-webcam";
import { setMeterReader } from "store/userSettingsSlice";
import http from "http";
import CustomButton from "./CustomButton";
import useForms from "hooks/useForms";
import { setShowLoading } from "store/sysSlice";
import CustomIconButton from "./CustomIconButton";
import { CameraAlt, Delete, FlashAuto, FlashlightOff, FlashlightOn, FlashOn } from "@mui/icons-material";
import NullCheck from "./NullCheck";

const MeterReader = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.userSettings?.meterReader);

  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const [image, setImage] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState(null);
  const [currentBox, setCurrentBox] = useState(null);
  const [flashOn, setFlashOn] = useState(false);

  useEffect(() => {
    drawBoxesOnCanvas();
  }, [boxes, currentBox]);

  useEffect(() => {
    drawImageOnCanvas();
  }, [image]);

  const captureImage = () => {
    const screenshot = webcamRef.current.getScreenshot();
    setImage(screenshot);
    if (flashOn)
      toggleFlash();
  };

  const startDrawing = (e) => {
    const { x, y } = getCoordinates(e);
    setStartPos({ x, y });
    setIsDrawing(true);
  };

  const drawBox = (e) => {
    if (!isDrawing || !startPos || boxes?.length >= 2) return;
    const { x, y } = getCoordinates(e);
    const width = Math.abs(x - startPos.x);
    const height = Math.abs(y - startPos.y);

    setCurrentBox({
      x: Math.min(startPos.x, x),
      y: Math.min(startPos.y, y),
      width,
      height,
      color: boxes.length === 0 ? "blue" : "green",
    });
  };

  const endDrawing = () => {
    if (currentBox) {
      setBoxes((prev) => [...prev, currentBox]);
      setCurrentBox(null);
    }
    setIsDrawing(false);
  };

  const drawBoxesOnCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.src = image;
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      [...boxes, currentBox].forEach((box) => {
        if (box) {
          ctx.strokeStyle = box.color;
          ctx.lineWidth = 2;
          ctx.strokeRect(box.x, box.y, box.width, box.height);
        }
      });
    };
  };

  const sendDataToServer = async () => {
    if (boxes.length < 2) {
      alert("יש לסמן שני אזורים: אחד למספר המונה ואחד לקריאה");
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // שלח את התמונה כ-Base64
    const imageData = canvas.toDataURL().split(',')[1];  // מסנן את החלק data:image/png;base64,

    const boxData = boxes?.map((box) => ({
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height,
      color: box.color,
    }));

    try {
      dispatch(setShowLoading(true))
      const response = await http.post('meterRead/meterProcessing', {
        imageBase64: imageData,
        boxes: boxData,
      });
      dispatch(setShowLoading(false))
      if (response?.data?.id === 1 && data?.callBack) {
        if (data.callBack) data.callBack(response.data.data);
        resetCamera()
        onClose();
      }
    } catch (error) {
      console.error("Error sending data to server:", error);
      alert("הייתה בעיה בשליחה. אנא נסה שנית.");
    }
  };

  const drawImageOnCanvas = () => {
    if (!canvasRef.current || !image) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.src = image;

    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.onerror = () => console.error("שגיאה בטעינת התמונה");
  };

  const getCoordinates = (e) => {
    let clientX, clientY;

    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const rect = e.target.getoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const onClose = () => {
    dispatch(setMeterReader({ open: false, callBack: () => { } }));
  };

  const deleteBoxe = () => {
    if (boxes.length === 1)
      setBoxes([])
    else
      setBoxes(data => data.splice(0, 1))
  }

  const resetCamera = () => {
    setBoxes([]);
    setImage(null);
  }

  const toggleFlash = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "environment" },
    });

    const track = stream.getVideoTracks()[0];
    const capabilities = track.getCapabilities();

    if (capabilities.torch) {
      await track.applyConstraints({ advanced: [{ torch: !flashOn }] });
      setFlashOn((bol) => !bol);
    } else {
      console.info("המכשיר לא תומך בפלאש.");
    }
  };

  return (
    <CustomDialog open={data?.open} title="סריקת מונה" onClose={onClose}>
      {!image
        ? <Stack spacing={2}>
          <Stack position='relative'>
            <Webcam
              audio={false}
              width={300}
              ref={webcamRef}
              screenshotFormat="image/png"
              videoConstraints={{ facingMode: "environment" }}
            />

            <Box position='absolute' bottom={0}>
              <CustomIconButton
                size={40}
                icon={flashOn ? <FlashlightOff /> : <FlashlightOn />}
                onClick={toggleFlash}
              />
            </Box>
          </Stack>
          <CustomButton title="צלם" onClick={captureImage} />
        </Stack>
        : <Stack spacing={2} style={{ position: "relative" }}>
          <Stack position='relative'>
            <canvas
              ref={canvasRef}
              width={300}
              height={400}
              style={{
                cursor: "crosshair",
                border: "1px solid black",
              }}
              onMouseDown={startDrawing}
              onMouseMove={drawBox}
              onMouseUp={endDrawing}
              onTouchStart={startDrawing}
              onTouchMove={drawBox}
              onTouchEnd={endDrawing}
              onTouchCancel={endDrawing}
            />

            <Stack direction='row' position='absolute' bottom={0} spacing={1}>
              <CustomIconButton
                size={40}
                icon={<CameraAlt />}
                onClick={resetCamera}
              />
              <NullCheck valueForCheck={boxes.length > 0}>
                <CustomIconButton
                  size={40}
                  icon={<Delete />}
                  onClick={deleteBoxe}
                />
              </NullCheck>
            </Stack>
          </Stack>

          <CustomButton title="שלח" onClick={sendDataToServer} />
        </Stack>
      }
    </CustomDialog>
  );
};

export default MeterReader;
